import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import utils from '../../../utils';
import type { Revision } from '../../../types';

const maxRevisionCount = 12;

export type BranchAndTagMap = {
  branch: Record<string, Revision[]>;
  tag: Record<string, Revision[]>;
};

export type GetProjectRevisionsParams = {
  projectLocator: string;
  revisionLocatorFilter?: string;
  refsType?: string;
  refName?: string;
  count?: number;
  source?: string;
  shouldFetchOnlyResolvedRevisions?: boolean;
};

const getProjectRevisions = ({
  projectLocator,
  revisionLocatorFilter,
  refsType,
  refName,
  count = maxRevisionCount,
  source,
  shouldFetchOnlyResolvedRevisions,
}: GetProjectRevisionsParams) => {
  return get<BranchAndTagMap>(`/api/projects/${utils.nuc(projectLocator)}/revisions`, false, {
    locator: revisionLocatorFilter,
    count,
    offset: 0,
    refs: [utils.nuc(refName || '')],
    refs_type: refsType,
    source,
    resolved: shouldFetchOnlyResolvedRevisions ? true : undefined,
  });
};

const getQueryKey = (params: GetProjectRevisionsParams): QueryKey => ['projects', params, 'revisions'];

/**
 * useProjectRevisions is a hook that fetches the revisions for a project.
 */
const useProjectRevisions = (params: GetProjectRevisionsParams, shouldFetch = true) => {
  return useQuery({
    queryKey: getQueryKey(params),
    queryFn: () => getProjectRevisions(params),
    enabled: shouldFetch,
  });
};

export default useProjectRevisions;
